import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Validators } from '@angular/forms';
import {
  ModalBodyComponent,
  ModalComponent,
  ModalFooterAction,
  ModalFooterComponent,
  ModalHeaderComponent,
} from '@groundworks/ngx-groundworks-ui';
import {
  CommissionPaymentResponse,
  CommissionPaymentType,
  CommissionWithSummary,
  RecordCommissionPaymentDto,
  UpdateCommissionPaymentDto,
} from '../../sdk/sdk';
import { COMMISSION_PAYMENT_TYPE_LABELS } from '../../constants/commission-payment-type.constants';
import { SELECTABLE_COMMISSION_PAYMENT_TYPES } from '../../utils/selectable-commission-payment-types';
import { CommissionService } from '../../services/commission.service';
import { commissionErrorMessage } from '../../utils/commission-error-message';
import { maxTwoDecimalPlaces } from '../../utils/form-validators';
import {
  defaultSignForType,
  magnitudeFromPercentOfCommission,
  percentOfCommissionFromMagnitude,
  signedPaymentAmount,
} from '../../utils/commission-rules';
import { formatUtcIsoDate, parseLocalIsoDate } from '../../utils/iso-date';

@Component({
  selector: 'app-commission-payment-modal',
  imports: [
    ReactiveFormsModule,
    ModalComponent,
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
  ],
  templateUrl: './commission-payment-modal.component.html',
  styleUrl: './commission-payment-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommissionPaymentModalComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly commissionService = inject(CommissionService);
  private readonly destroyRef = inject(DestroyRef);

  readonly commission = signal<CommissionWithSummary | null>(null);

  readonly saved = output<void>();
  readonly cancelled = output<void>();

  readonly saving = signal(false);
  readonly errorMessage = signal<string | null>(null);

  readonly allowedPaymentTypes = signal<CommissionPaymentType[]>(SELECTABLE_COMMISSION_PAYMENT_TYPES);
  readonly typeLabels = COMMISSION_PAYMENT_TYPE_LABELS;

  readonly editingPayment = signal<CommissionPaymentResponse | null>(null);
  readonly isEditMode = computed(() => this.editingPayment() !== null);

  readonly title = computed(() => (this.isEditMode() ? 'Edit Payment' : 'Record Payment'));

  // Cancel first, then the primary — the ordering every other groundworks-modal-footer on the app
  // uses. The save button carries the same gate the old inline submit button did.
  readonly footerActions = computed<ModalFooterAction[]>(() => [
    {
      label: 'Cancel',
      type: 'outline-secondary',
      action: () => this.cancel(),
    },
    {
      label: this.isEditMode() ? 'Save Changes' : 'Save Payment',
      type: 'primary',
      action: () => this.submit(),
      loading: () => this.saving(),
      disabled: () => this.form.invalid || this.saving(),
    },
  ]);

  get paymentTypes(): CommissionPaymentType[] {
    return this.allowedPaymentTypes();
  }

  readonly form = this.formBuilder.group({
    type: this.formBuilder.control<CommissionPaymentType | null>(null, Validators.required),
    sign: this.formBuilder.control<'positive' | 'negative' | null>(null, Validators.required),
    magnitude: this.formBuilder.control<number | null>(null, [Validators.required, maxTwoDecimalPlaces]),
    percentOfCommission: this.formBuilder.control<number | null>(null),
    // Required, but deliberately NOT defaulted to today — an admin sets the date consciously.
    qualificationDate: this.formBuilder.control<string | null>(null, Validators.required),
    notes: this.formBuilder.control<string | null>(null),
  });

  private readonly magnitudeValue = toSignal(this.form.controls.magnitude.valueChanges, {
    initialValue: this.form.controls.magnitude.value,
  });
  private readonly signValue = toSignal(this.form.controls.sign.valueChanges, {
    initialValue: this.form.controls.sign.value,
  });
  private readonly typeValue = toSignal(this.form.controls.type.valueChanges, {
    initialValue: this.form.controls.type.value,
  });

  readonly showDirection = computed(() => {
    const type = this.typeValue();
    return type != null && defaultSignForType(type) === null;
  });

  readonly alreadyPaidNet = computed(() => this.commission()?.summary?.amountPaid ?? 0);
  readonly commissionTotal = computed(() => this.commission()?.commission?.amount ?? 0);

  readonly resultAmount = computed(() => {
    const magnitude = this.magnitudeValue();
    const sign = this.signValue();
    if (magnitude == null) {
      return null;
    }
    return signedPaymentAmount(magnitude, sign);
  });

  readonly projectedNetPaid = computed(() => {
    const amount = this.resultAmount();
    return amount == null ? null : this.alreadyPaidNet() + amount;
  });

  private readonly amountInput = viewChild<ElementRef<HTMLInputElement>>('amountInput');

  private updatingFromPercent = false;

  constructor() {
    this.form.controls.type.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((type) => {
      this.form.controls.sign.setValue(type == null ? null : defaultSignForType(type));
    });

    this.form.controls.percentOfCommission.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((percent) => {
        if (percent == null) {
          return;
        }
        const magnitude = magnitudeFromPercentOfCommission(percent, this.commissionTotal());
        this.updatingFromPercent = true;
        this.form.controls.magnitude.setValue(magnitude);
        this.updatingFromPercent = false;
        this.formatAmount();
      });

    this.form.controls.magnitude.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((magnitude) => {
      if (this.updatingFromPercent) {
        return;
      }
      const percent =
        magnitude == null ? null : percentOfCommissionFromMagnitude(magnitude, this.commissionTotal());
      this.form.controls.percentOfCommission.setValue(percent, { emitEvent: false });
    });
  }

  formatAmount(): void {
    const control = this.form.controls.magnitude;
    const input = this.amountInput()?.nativeElement;
    if (input == null || control.invalid || control.value == null) {
      return;
    }
    input.value = control.value.toFixed(2);
  }

  /**
   * `qualificationDate` is a `DateOnly` on the server, so the JSON is a bare `"2026-08-14"` and the
   * generated client turns it into `new Date("2026-08-14")` — UTC midnight. Reading it back with
   * local getters (`formatLocalIsoDate`) lands on the previous day anywhere west of UTC, which is
   * why reopening a payment used to show 08/13 for a date entered as 08/14. `formatUtcIsoDate`
   * reads the same calendar day the server sent, matching every other date-only prefill in the app.
   */
  startEdit(payment: CommissionPaymentResponse): void {
    this.editingPayment.set(payment);
    const magnitude = Math.abs(payment.amount ?? 0);
    this.form.patchValue({
      type: payment.type ?? null,
      sign: (payment.amount ?? 0) < 0 ? 'negative' : 'positive',
      magnitude,
      qualificationDate: payment.qualificationDate ? formatUtcIsoDate(payment.qualificationDate) : null,
      notes: payment.notes ?? null,
    });
    this.formatAmount();
  }

  submit(): void {
    const amount = this.resultAmount();
    if (this.form.invalid || amount == null) {
      return;
    }

    this.errorMessage.set(null);
    this.saving.set(true);

    const value = this.form.getRawValue();
    const editing = this.editingPayment();
    if (editing) {
      const updateDto = new UpdateCommissionPaymentDto({
        type: value.type ?? undefined,
        amount,
        payDate: editing.payDate,
        qualificationDate: value.qualificationDate ? parseLocalIsoDate(value.qualificationDate) : undefined,
        notes: value.notes ?? undefined,
      });
      this.commissionService
        .updatePayment(editing.id ?? '', updateDto)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.saving.set(false);
            this.saved.emit();
          },
          error: (err: unknown) => {
            this.saving.set(false);
            this.errorMessage.set(commissionErrorMessage(err));
          },
        });
      return;
    }

    this.commissionService
      .recordPayment(this.commission()?.commission?.id ?? '', this.buildPaymentDto(amount))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.saved.emit();
        },
        error: (err: unknown) => {
          this.saving.set(false);
          this.errorMessage.set(commissionErrorMessage(err));
        },
      });
  }

  /**
   * The user picks ONE date, labelled "Date Qualified", and it is written to BOTH
   * `qualificationDate` and `payDate` as the same calendar date.
   *
   * ⚠️ The duplication is deliberate — do not "simplify" it away. The create endpoint still
   * rejects a payment with no `payDate` ("'payDate' is required."), and relaxing that guard is a
   * deferred schema change. Dropping either assignment makes every Record Payment return 400.
   * The consolidation is presentational: one field for the user, two fields on the wire.
   *
   * `parseLocalIsoDate` (never `new Date(iso)`) keeps the picked day intact — `new Date('2026-08-01')`
   * is UTC midnight, and the generated `formatDate()` reads local getters, so west of UTC the wire
   * value would land on the previous day.
   */
  private buildPaymentDto(amount: number): RecordCommissionPaymentDto {
    const value = this.form.getRawValue();
    const dateQualified = value.qualificationDate ? parseLocalIsoDate(value.qualificationDate) : undefined;

    return new RecordCommissionPaymentDto({
      type: value.type ?? undefined,
      amount,
      qualificationDate: dateQualified,
      payDate: dateQualified,
      notes: value.notes ?? undefined,
      approved: false,
    });
  }

  cancel(): void {
    this.cancelled.emit();
  }
}
